import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Slider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const NarrationPlayer = ({ narrationUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const rewind10 = () => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = Math.max(0, audio.currentTime - 10);
  };

  const forward10 = () => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = Math.min(audio.duration, audio.currentTime + 10);
  };

  const restartAudio = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleSpeedChange = (event, newValue) => {
    setPlaybackRate(newValue);
    if (audioRef.current) {
      audioRef.current.playbackRate = newValue;
    }
  };

  if (!narrationUrl) return null;

  return (
    <Box
      sx={{
        backgroundColor: '#fef6e4',
        border: '2px solid #fcd5ce',
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
        mb: 2,
        fontFamily: "'Comic Neue', cursive",
      }}
    >
      <Box sx={{ mb: { xs: 2, md: 0 } }}>
        <Typography variant="h6" fontWeight="bold" sx={{ color: '#ff9f1c' }}>
          🎧 Hear this lesson
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Press play to hear this lesson read this out loud
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <audio
          ref={audioRef}
          src={narrationUrl}
          onEnded={() => setIsPlaying(false)}
          preload="auto"
        />

        <Tooltip title="Restart">
          <IconButton onClick={restartAudio} sx={{ color: '#e76f51' }}>
            <RestartAltIcon fontSize="medium" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Rewind 10s">
          <IconButton onClick={rewind10} sx={{ color: '#457b9d' }}>
            <Replay10Icon fontSize="medium" />
          </IconButton>
        </Tooltip>

        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <IconButton
            onClick={togglePlayback}
            sx={{
              backgroundColor: '#ffffff',
              border: '2px solid #ffbf69',
              color: '#264653',
              '&:hover': {
                backgroundColor: '#fff2e6',
              },
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Forward 10s">
          <IconButton onClick={forward10} sx={{ color: '#2a9d8f' }}>
            <Forward10Icon fontSize="medium" />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: 140, ml: 2 }}>
          <Typography
            variant="caption"
            sx={{
              textAlign: 'center',
              display: 'block',
              mb: 0.5,
              fontWeight: 'bold',
              color: '#6d6875',
            }}
          >
            Speed: {playbackRate.toFixed(1)}x
          </Typography>
          <Slider
            size="small"
            value={playbackRate}
            min={0.5}
            max={2}
            step={0.1}
            onChange={handleSpeedChange}
            aria-label="Playback Speed"
            sx={{
              color: '#ff9f1c',
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default NarrationPlayer;
